import { put, take, fork, call, all, select } from "redux-saga/effects";
import { Ace2WarrntyItemAction, Ace2WarrntyItemScope } from "./types";
import {
  enableScopeLoading,
  disableScopeLoading,
} from "../scope-loading/actions";
import * as Api from "../../service/api";
import { ScopeErrorAction } from "../scope-error/types";
import { mapApiFlatData, nextStepRedirect } from "./helpers";
import { RootState } from "../root-reducer";
import { setInvoiceURL, showSuccesModal } from "./actions";
import toast from "react-hot-toast";
import { getPreSignedUrl } from "../../helper";
import axios from "axios";

function* fetchHelperData() {
  while (true) {
    yield take(Ace2WarrntyItemAction.FETCH_OPTIONS);
    yield put(enableScopeLoading(Ace2WarrntyItemScope));

    try {
      const [damageItemTypes, colors, materialTypes, manufacturers] = yield all(
        [
          call(Api.ace2ClaimOptions.damageItemTypes),
          call(Api.ace2ClaimOptions.colors),
          call(Api.ace2ClaimOptions.materialTypes),
          call(Api.ace2ClaimOptions.manufacturers),
        ]
      );

      yield put({
        type: Ace2WarrntyItemAction.SET_OPTIONS,
        payload: {
          claimItemTypes: damageItemTypes?.map(mapApiFlatData),
          materialTypes: materialTypes?.map(mapApiFlatData),
          manufacturers: manufacturers?.map(mapApiFlatData),
          colors: colors?.map(mapApiFlatData),
        },
      });
    } catch (error) {
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: Ace2WarrntyItemScope,
        error,
      });
    } finally {
      yield put(disableScopeLoading(Ace2WarrntyItemScope));
    }
  }
}

function* addWarrntyItemFlow() {
  while (true) {
    const request = yield take(Ace2WarrntyItemAction.ADD);
    yield put(enableScopeLoading(Ace2WarrntyItemScope));
    try {
      const ace2WarrntyItem = yield select(
        (state: RootState) => state.ace2WarrntyItem
      );
      const warranty_id = ace2WarrntyItem.warrantyId;
      const data = yield call(
        Api.warranty.insertAce2WarrntyItem,
        warranty_id,
        request.payload
      );
      if (data?.code === "SUCCESS") {
        yield put(showSuccesModal());
      } else {
        toast.error("Something went wrong");
      }
    } catch (error) {
      toast.error(error?.message || "Something went wrong", {
        position: "bottom-center",
      });
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: Ace2WarrntyItemScope,
        error,
      });
    } finally {
      yield put(disableScopeLoading(Ace2WarrntyItemScope));
    }
  }
}

function* uploadInvoiceFlow() {
  while (true) {
    const request = yield take(Ace2WarrntyItemAction.UPLOAD_INVOICE);
    yield put(enableScopeLoading(Ace2WarrntyItemScope));
    try {
      const files = request.payload;
      const ace2WarrntyItem = yield select(
        (state: RootState) => state.ace2WarrntyItem
      );
      const claimId = ace2WarrntyItem.claimId;
      const fileNames = yield all(
        files.map(function* (item: File) {
          const ext = item.name
            .substring(item.name.lastIndexOf(".") + 1)
            .toLowerCase();
          const fileType =
            ext === "heic" ? "image/heic" : item.type || "image/jpeg";
          const { url } = yield call(
            Api.fileAce2Claim.getPreSignedUrl,
            claimId,
            fileType
          );
          yield axios.put(url, item, {
            headers: {
              "Content-Type": fileType,
            },
          });
          const data = new URL(url);
          data.search = "";
          const uploadedName = data.toString().split("/").pop() || "";
          return uploadedName.replace(/\.heic$/i, ".png");
        })
      );
      if (fileNames.length > 0) {
        nextStepRedirect(2);
        yield put(
          setInvoiceURL(getPreSignedUrl({ claimId, fileName: fileNames[0] }))
        );
        toast.success("file uploaded sucessfully", {
          position: "bottom-center",
        });
      }
    } catch (error) {
      toast.error("file not uploaded.", {
        position: "bottom-center",
      });
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: Ace2WarrntyItemScope,
        error,
      });
    } finally {
      yield put(disableScopeLoading(Ace2WarrntyItemScope));
    }
  }
}

export default function* ace2WarrntyItemSaga() {
  yield fork(fetchHelperData);
  yield fork(addWarrntyItemFlow);
  yield fork(uploadInvoiceFlow);
}
